import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 1800);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen flex items-center justify-center px-4">
      <div className="jpd-card w-full max-w-md p-8 sm:p-10">
        {done ? (
          <div className="text-center" data-testid="reset-password-success">
            <span className="mx-auto w-14 h-14 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
            </span>
            <h1 className="mt-5 text-xl font-semibold">Password updated</h1>
            <p className="mt-2 text-sm text-slate-400">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="text-xl sm:text-2xl font-semibold">Choose a new password</h1>
            {!token && (
              <p data-testid="reset-password-no-token" className="mt-2 text-sm text-[#EF4444]">
                This reset link is missing its token — request a new one.
              </p>
            )}
            <form data-testid="reset-password-form" onSubmit={submit} className="mt-6 space-y-4">
              <input
                data-testid="reset-password-new-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password (min 8 characters)"
                className="jpd-input"
                autoComplete="new-password"
              />
              <input
                data-testid="reset-password-confirm-input"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                className="jpd-input"
                autoComplete="new-password"
              />
              {error && (
                <p data-testid="reset-password-error" className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30  px-4 py-2.5">
                  {error}
                </p>
              )}
              <button
                data-testid="reset-password-submit-button"
                disabled={loading || !token}
                className="w-full bg-primary hover:bg-[#C20A0B] disabled:opacity-60 text-white font-semibold py-3.5  transition-colors"
              >
                {loading ? "Updating…" : "Update Password"}
              </button>
            </form>
          </>
        )}
        <Link to="/admin/login" data-testid="reset-password-back-link" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
